import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Typography,
  useTheme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import {
  type ActivityNextStepAction,
  ActivityNextStepsDialog,
} from '@modules/_shared/components/feed/ActivityNextStepsDialog';
import { createActivityNextStepOptions } from '@modules/_shared/components/feed/activityOutcomeShared';
import type {
  ActivityFollowUpContext,
  OutcomeFollowUpAction,
} from '@modules/_shared/components/feed/activityOutcomeShared';
import type { PipelineLeadType } from '@modules/_shared/components/feed/types';
import { GET_PENDING_ACTIVITIES } from '@modules/_shared/components/feed/utils/activityQueries';
import { createNextStepsCloseHandler } from '@modules/_shared/components/feed/utils/outcomeDialogHelpers';
import { MarkdownComposer } from '@modules/_shared/components/MarkdownComposer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import type { PendingPlanActivity } from '@modules/lead-details/hooks/usePlanActivityConflict';
import { useLocale } from '@shared/hooks/locale';

type CallOutcomeDialogFlowProps = {
  open: boolean;
  activity: PendingPlanActivity | null;
  onClose: () => void;
  onFollowUpActionSelect?: (action: OutcomeFollowUpAction) => void;
  canMarkLeadLost?: boolean;
  autoOutcome?: 'completed' | 'cancelled' | null;
  parentType?: null | 'Enquiry' | 'Lot' | 'Lead' | 'User' | 'BuyerLead';
  activityId?: string | null;
  pipelineType: PipelineLeadType;
  onLoadingChange?: (loading: boolean) => void;
};

const COMPLETE_CALL = gql(/* GraphQL */ `
  mutation CallOutcomeDialogFlowCompleteCall(
    $id: uuid!
    $success: Boolean!
    $note: String
  ) {
    update_activities_by_pk(
      pk_columns: { id: $id }
      _set: { done: true, success: $success, done_at: "now()", note: $note }
    ) {
      id
    }
  }
`);

const GET_CALL_ACTIVITY = gql(/* GraphQL */ `
  query CallOutcomeDialogFlowGetActivity($id: uuid!) {
    activities_by_pk(id: $id) {
      id
      note
    }
  }
`);

type HandleCancelOptions = {
  preserveAutoHandled?: boolean;
};

export const CallOutcomeDialogFlow = ({
  open,
  activity,
  activityId = null,
  onClose,
  onFollowUpActionSelect,
  canMarkLeadLost = false,
  autoOutcome = null,
  parentType,
  pipelineType,
  onLoadingChange,
}: CallOutcomeDialogFlowProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);
  const { control, reset, clearErrors, handleSubmit } = useForm<{
    note: string;
  }>({
    defaultValues: {
      note: '',
    },
  });

  const effectiveActivityId = activity?.id ?? activityId ?? null;

  const { data: activityData, error: activityError } = useQuery(
    GET_CALL_ACTIVITY,
    {
      skip: !open || !effectiveActivityId || activity != null,
      variables: { id: effectiveActivityId! },
    },
  );

  // Apollo v4 removed `onError` from `useQuery`; surface query errors via the
  // same snackbar that mutation onError callbacks feed.
  useEffect(() => {
    if (activityError) {
      setErrorLike(activityError);
    }
  }, [activityError]);

  const activityNote =
    activity?.note ?? activityData?.activities_by_pk?.note ?? null;

  const [stage, setStage] = useState<'outcome' | 'details'>('outcome');

  const [nextStepsContext, setNextStepsContext] =
    useState<ActivityFollowUpContext<PendingPlanActivity | null> | null>(null);
  const [autoHandled, setAutoHandled] = useState(false);
  const selectedActionRef = useRef<OutcomeFollowUpAction | null>(null);
  const skipOutcomeDialog =
    autoOutcome === 'cancelled' || autoOutcome === 'completed';

  const isBrokeragePipeline = pipelineType === 'sales';

  const handleCancel = useCallback(
    ({ preserveAutoHandled = false }: HandleCancelOptions = {}) => {
      setStage('outcome');
      reset({ note: activityNote ?? '' });
      clearErrors('note');
      if (!preserveAutoHandled) {
        setAutoHandled(false);
      }
      onClose();
    },
    [reset, activityNote, clearErrors, onClose],
  );

  const [completeCall, { loading }] = useMutation(COMPLETE_CALL, {
    refetchQueries: [GET_PENDING_ACTIVITIES],
    onError: setErrorLike,
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (autoOutcome === 'cancelled') {
      onLoadingChange?.(loading);
    }
  }, [loading, autoOutcome, onLoadingChange]);

  useEffect(() => {
    setAutoHandled(false);
    setStage('outcome');
    reset({ note: (open ? activityNote : null) ?? '' });
    clearErrors('note');
  }, [open, activityNote, reset, clearErrors]);

  const submitOutcome = useCallback(
    async ({
      success,
      note: submittedNote,
      onCompleted,
    }: {
      success: boolean;
      note?: string | null;
      onCompleted?: () => void;
    }) => {
      if (!effectiveActivityId) {
        onClose();
        return;
      }

      await completeCall({
        variables: {
          id: effectiveActivityId,
          success,
          note: submittedNote ?? null,
        },
      });

      emitFeedRefresh();

      onCompleted?.();
    },
    [completeCall, effectiveActivityId, onClose],
  );

  const handleNotReached = useCallback(async () => {
    if (!effectiveActivityId) {
      onClose();
      return;
    }

    const followUpActivity = activity ?? {
      id: effectiveActivityId,
      note: activityNote ?? undefined,
    };

    await submitOutcome({
      success: false,
      note: activityNote,
      onCompleted: () => {
        setNextStepsContext({
          task: followUpActivity,
          success: false,
          isBrokeragePipeline,
        });
      },
    });
  }, [
    activity,
    activityNote,
    effectiveActivityId,
    submitOutcome,
    onClose,
    isBrokeragePipeline,
  ]);

  const handleReached = () => {
    if (!effectiveActivityId) {
      onClose();
      return;
    }
    setStage('details');
    reset({ note: activityNote ?? '' });
    clearErrors('note');
  };

  const handleConfirm = handleSubmit(async data => {
    if (!effectiveActivityId) {
      onClose();
      return;
    }

    const trimmed = data.note.trim();

    const followUpActivity = activity ?? {
      id: effectiveActivityId,
      note: trimmed.length > 0 ? trimmed : undefined,
    };

    await submitOutcome({
      success: true,
      note: trimmed,
      onCompleted: () => {
        setNextStepsContext({
          task: followUpActivity,
          success: true,
          isBrokeragePipeline,
        });
        setStage('details');
        reset({ note: trimmed });
        clearErrors('note');
      },
    });
  });

  const handleCloseNextSteps = useCallback(() => {
    setNextStepsContext(null);
    setStage('outcome');
    handleCancel({ preserveAutoHandled: autoHandled });
  }, [autoHandled, handleCancel]);

  const handleNextStepSelect = useCallback(
    (action: ActivityNextStepAction) => {
      if (!nextStepsContext) {
        return;
      }

      // Type guard to ensure action is valid for call outcomes
      const validActions: ActivityNextStepAction[] = [
        'schedule_visit',
        'schedule_call',
        'schedule_task',
        'lead_lost',
        'lead_won',
        'note',
        'do_nothing',
      ];

      if (!validActions.includes(action)) {
        return;
      }

      selectedActionRef.current = action as OutcomeFollowUpAction;

      onFollowUpActionSelect?.(action as OutcomeFollowUpAction);

      setStage('outcome');
      setNextStepsContext(null);

      handleCancel({ preserveAutoHandled: autoHandled });
    },
    [autoHandled, nextStepsContext, onFollowUpActionSelect, handleCancel],
  );

  const followUpSuccess = nextStepsContext?.success ?? false;
  const followUpBrokerage = nextStepsContext?.isBrokeragePipeline ?? false;

  const palette = theme.palette;
  const nextStepOptions = useMemo(() => {
    return createActivityNextStepOptions({
      t,
      palette,
      canMarkLeadLost,
      followUpSuccess,
      followUpBrokerage,
      parentType,
    });
  }, [
    canMarkLeadLost,
    followUpBrokerage,
    followUpSuccess,
    palette,
    t,
    parentType,
  ]);

  useEffect(() => {
    if (!open || autoHandled) {
      return;
    }

    if (autoOutcome === 'completed') {
      setAutoHandled(true);
      setStage('details');
      return;
    }

    if (autoOutcome === 'cancelled') {
      setAutoHandled(true);
      void handleNotReached();
    }
  }, [autoOutcome, handleNotReached, open, autoHandled]);

  const outcomeDialogOpen =
    open &&
    stage === 'outcome' &&
    !skipOutcomeDialog &&
    nextStepsContext == null;
  const detailsDialogOpen =
    open &&
    stage === 'details' &&
    nextStepsContext == null &&
    autoOutcome !== 'cancelled';
  const nextStepsDialogOpen = Boolean(nextStepsContext);

  return (
    <>
      <SlideDialog
        open={outcomeDialogOpen}
        onClose={() => handleCancel()}
        title={t('Did you reach the client?')}
        disableCancel
        customPrimaryActions={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button disabled={loading} onClick={() => handleCancel()}>
              {t('Cancel')}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleNotReached}
              loading={loading}
              loadingPosition="start"
            >
              {t('Not Reached')}
            </Button>
            <Button
              color="success"
              variant="contained"
              disabled={loading}
              onClick={handleReached}
            >
              {t('Reached')}
            </Button>
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">
            {t('Indicate if the client picked up.')}
          </Typography>
        </Box>
      </SlideDialog>

      <SlideDialog
        open={detailsDialogOpen}
        onClose={() => handleCancel()}
        title={t('Successful call')}
        onSave={handleConfirm}
        loading={loading}
      >
        <Box sx={{ p: 2 }}>
          <Controller
            name="note"
            control={control}
            rules={{
              validate: value => {
                const trimmed = (value ?? '').trim();
                return trimmed.length > 0 || t('Missing comment');
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <MarkdownComposer
                  placeholder={t('What happened during the call?')}
                  initialValue={value}
                  format="html"
                  preserveLineBreaks
                  readOnly={loading}
                  minHeight="130px"
                  onChange={onChange}
                />
                {error && (
                  <FormHelperText error={!!error}>
                    {error.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
      </SlideDialog>

      <MutationErrorSnackbar
        error={errorLike}
        onClose={() => setErrorLike(null)}
      />

      <ActivityNextStepsDialog
        open={nextStepsDialogOpen}
        onClose={createNextStepsCloseHandler(
          selectedActionRef,
          handleCloseNextSteps,
        )}
        title={
          followUpSuccess
            ? t('Successful call')
            : t('Call attempt unsuccessful')
        }
        options={nextStepOptions}
        onSelect={handleNextStepSelect}
      />
    </>
  );
};
